/**
 * KOPS-1 Phase A1 · Kitchen Expediter.
 *
 * Real-time pass view for the expo, driven entirely by LIVE F&B orders:
 *   • Live queue sorted by priority + created time
 *   • Priority chips + SLA color coding + elapsed timer per order
 *   • Plated / Picked Up / Hold-at-Pass controls
 *   • Missing item + Allergy alert flags
 *   • Course fire / hold / release controls per order
 *
 * Data source: GET /api/kops/v1/expo/queue (real fnb_orders + fnb_tickets).
 * Subscribes to /api/kops/v1/stream (SSE) for push updates and falls back to
 * an 8s poll if the stream errors so nothing ever silently stops updating.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Utensils, Bell, AlertTriangle, ShieldAlert, Clock, PlayCircle,
  PauseCircle, PackageCheck, HandPlatter, MinusCircle, Loader2, ChefHat,
} from "lucide-react";
import api from "@/lib/api";

const PRIORITY_META = {
  vip:          { label: "VIP",          tone: "bg-purple-100 text-purple-800 border-purple-200" },
  rush:         { label: "RUSH",         tone: "bg-red-100 text-red-800 border-red-200" },
  room_service: { label: "IN-ROOM",      tone: "bg-blue-100 text-blue-800 border-blue-200" },
  pool:         { label: "POOL",         tone: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  takeaway:     { label: "TAKEAWAY",     tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  buffet:       { label: "BUFFET",       tone: "bg-orange-100 text-orange-800 border-orange-200" },
  normal:       { label: "NORMAL",       tone: "bg-slate-100 text-slate-700 border-slate-200" },
};

function slaBand(order) {
  const created = new Date(order.created_at).getTime();
  const now     = Date.now();
  const elapsed = (now - created) / 1000;
  const sla     = order.tickets?.[0]?.sla_seconds || 900;
  const pct     = elapsed / sla;
  if (pct >= 0.9) return { color: "border-red-400 bg-red-50", text: "text-red-700", pct, elapsed };
  if (pct >= 0.5) return { color: "border-amber-300 bg-amber-50", text: "text-amber-700", pct, elapsed };
  return { color: "border-emerald-300 bg-emerald-50", text: "text-emerald-700", pct, elapsed };
}

function fmtElapsed(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function KitchenExpediter() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kops/v1/expo/queue");
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      /* keep last snapshot on error */
    } finally {
      setLoading(false);
    }
  }, []);

  // Live timer tick
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // SSE stream — auto-refresh on any KOPS event, with an 8s backup poll.
  useEffect(() => {
    load();
    const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    try {
      const es = new EventSource(`${baseUrl}/api/kops/v1/stream`, { withCredentials: true });
      esRef.current = es;
      es.addEventListener("open", () => setConnected(true));
      const refresh = () => load();
      ["ticket.created", "ticket.line_status", "course.fired", "course.held",
       "course.released", "expo.plated", "expo.picked_up",
       "expo.flag.allergy", "expo.flag.missing"].forEach((ev) => {
        es.addEventListener(ev, refresh);
      });
      es.addEventListener("error", () => {
        setConnected(false);
        // Fall back to poll while stream is down
      });
    } catch {
      setConnected(false);
    }
    // Backup poll every 8s in case SSE flakes
    const poll = setInterval(load, 8000);
    return () => {
      if (esRef.current) esRef.current.close();
      clearInterval(poll);
    };
  }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="kitchen-expediter">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
            Kitchen Operations · Phase A1
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <HandPlatter className="w-7 h-7 text-slate-700" />
            Kitchen Expediter
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Live pass view — plate, hand off, and manage flags in real time.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[12px]">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-medium
                            ${connected
                              ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                              : "bg-slate-100 border-slate-200 text-slate-500"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
            {connected ? "Live" : "Polling"}
          </span>
          <span className="text-slate-500">{orders.length} order{orders.length === 1 ? "" : "s"} on pass</span>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading pass…
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-16 text-center">
          <ChefHat className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="text-lg font-semibold text-slate-700 mt-3">Pass is clear</h3>
          <p className="text-slate-500 text-sm mt-1">
            No orders currently need expediting. New tickets appear here the moment they&apos;re ready.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {orders.map((o) => <OrderCard key={o.order_id} o={o} now={now} onChange={load} />)}
        </div>
      )}
    </div>
  );
}


function OrderCard({ o, now, onChange }) {
  const priority = PRIORITY_META[o.priority] || PRIORITY_META.normal;
  const created  = new Date(o.created_at).getTime();
  const elapsed  = (now - created) / 1000;
  const sla      = o.tickets?.[0]?.sla_seconds || 900;
  const band     = slaBand(o);
  const progress = o.total_lines > 0 ? Math.round((o.ready_count / o.total_lines) * 100) : 0;

  const act = async (action) => {
    try {
      await api.post(`/kops/v1/expo/orders/${o.order_id}/action`, { action });
      toast.success(action === "plated" ? "Plated" : action === "picked_up" ? "Picked up" : "Updated");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const flag = async (kind) => {
    const note = window.prompt(`${kind === "allergy" ? "Allergy alert" : "Missing item"} — details`);
    if (!note) return;
    try {
      await api.post(`/kops/v1/expo/orders/${o.order_id}/flag`, { kind, note });
      toast.success("Flag recorded");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className={`rounded-xl border-2 bg-white p-4 shadow-sm ${band.color}`}
         data-testid={`expo-order-${o.order_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold ${priority.tone}`}>
              {priority.label}
            </span>
            {o.has_allergy && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold">
                <ShieldAlert className="w-3 h-3" />
                ALLERGY
              </span>
            )}
            {o.expo_status === "hold_at_pass" && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-500 text-white text-[10px] font-bold">
                HELD
              </span>
            )}
          </div>
          <div className="mt-1.5 font-semibold text-slate-900 truncate">
            {o.order_code}
            {o.room_number && <span className="text-slate-500 font-normal"> · Room {o.room_number}</span>}
            {o.table_id     && <span className="text-slate-500 font-normal"> · Table {o.table_id}</span>}
          </div>
          {o.guest_name && (
            <div className="text-slate-500 text-[13px] truncate">{o.guest_name}</div>
          )}
        </div>
        <div className={`text-right shrink-0 ${band.text}`}>
          <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide justify-end">
            <Clock className="w-3.5 h-3.5" />
            Elapsed
          </div>
          <div className="text-xl font-bold tabular-nums">{fmtElapsed(elapsed)}</div>
          <div className="text-[10px] text-slate-500 tabular-nums">SLA {fmtElapsed(sla)}</div>
        </div>
      </div>

      {o.allergy_notes && (
        <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          {o.allergy_notes}
        </div>
      )}

      {/* Progress */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
          <span>Ready {o.ready_count}/{o.total_lines}</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div className={`h-full transition-all ${progress === 100 ? "bg-emerald-500" : "bg-amber-500"}`}
               style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Tickets w/ lines */}
      <div className="mt-3 space-y-1.5">
        {(o.tickets || []).slice(0, 3).map((t) => (
          <div key={t.id} className="text-[12px] text-slate-700">
            <div className="font-medium text-slate-500 text-[10px] uppercase">
              {t.code} · Course {t.course_number}
            </div>
            {(t.lines || []).map((L) => (
              <div key={L.line_id} className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0
                  ${L.production_status === "ready" ? "bg-emerald-500"
                  : L.production_status === "preparing" ? "bg-amber-500"
                  : L.production_status === "served" ? "bg-slate-400"
                  : "bg-slate-300"}`} />
                <span className="truncate">
                  {L.qty}× {L.menu_item_name}
                </span>
                {L.special_instructions && (
                  <span className="text-[10px] text-slate-500 italic">— {L.special_instructions}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          onClick={() => act("plated")}
          disabled={o.expo_status === "plated" || o.expo_status === "delivered"}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md
                     bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600
                     disabled:bg-slate-200 disabled:text-slate-400 transition-colors"
          data-testid={`expo-plate-${o.order_id}`}
        >
          <Utensils className="w-4 h-4" /> Plated
        </button>
        <button
          onClick={() => act("picked_up")}
          disabled={o.expo_status === "delivered"}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md
                     bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700
                     disabled:bg-slate-200 disabled:text-slate-400 transition-colors"
          data-testid={`expo-pickup-${o.order_id}`}
        >
          <PackageCheck className="w-4 h-4" /> Picked up
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button onClick={() => flag("missing")}
                className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md
                           bg-white border border-slate-200 text-slate-700 text-[12px] hover:bg-slate-50"
                data-testid={`expo-flag-missing-${o.order_id}`}>
          <MinusCircle className="w-3.5 h-3.5" /> Missing
        </button>
        <button onClick={() => flag("allergy")}
                className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md
                           bg-white border border-red-200 text-red-700 text-[12px] hover:bg-red-50"
                data-testid={`expo-flag-allergy-${o.order_id}`}>
          <ShieldAlert className="w-3.5 h-3.5" /> Allergy
        </button>
      </div>

      {/* Flags list */}
      {(o.expo_flags || []).length > 0 && (
        <div className="mt-3 space-y-1">
          {o.expo_flags.slice(-3).map((f, i) => (
            <div key={i} className="text-[11px] text-slate-600 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0 mt-0.5" />
              <span><span className="font-semibold">{f.kind}:</span> {f.note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
